// Check if wishlist already exists to prevent duplicate initialization
if (typeof window.WishlistManager === 'undefined') {
  class WishlistManager {
  constructor() {
    this.COOKIE_NAME = 'shopify_wishlist';
    this.COOKIE_EXPIRY_DAYS = 365;
    this.API_ENDPOINT = '/apps/wishlist';
    this.items = [];
    this.isInitialized = false;
    
    this.init();
  }
  
  init() {
    console.log('🔥 Wishlist Manager: Initializing...');
    this.loadWishlist();
    this.bindEvents();
    this.updateUI();
    this.syncWithKlaviyo();
    
    // Check for shared wishlist in URL
    this.checkSharedWishlist();
    
    // Sync with server if logged in
    if (window.ShopifyAnalytics?.meta?.page?.customerId) {
      this.syncWithServer();
    }
    
    this.isInitialized = true;
    console.log('✅ Wishlist Manager: Initialized with', this.items.length, 'items');
  }
  
  // Cookie Management
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }
  
  setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/`;
  }
  
  // Wishlist Data Management
  loadWishlist() {
    console.log('🔍 Wishlist: Loading from cookie...');
    const cookieData = this.getCookie(this.COOKIE_NAME);
    if (cookieData) {
      try {
        this.items = JSON.parse(decodeURIComponent(cookieData));
        console.log('✅ Wishlist: Loaded', this.items.length, 'items from cookie');
      } catch (e) {
        console.error('❌ Wishlist: Failed to parse cookie data', e);
        this.items = [];
      }
    } else {
      console.log('ℹ️ Wishlist: No cookie found, starting with empty wishlist');
    }
  }
  
  saveWishlist() {
    console.log('💾 Wishlist: Saving', this.items.length, 'items to cookie');
    const data = encodeURIComponent(JSON.stringify(this.items));
    this.setCookie(this.COOKIE_NAME, data, this.COOKIE_EXPIRY_DAYS);
    console.log('🍪 Wishlist: Cookie saved, size:', data.length, 'bytes');
    this.updateUI();
    this.syncWithKlaviyo();
  }
  
  addItem(productData) {
    console.log('➕ Wishlist: Adding item', productData);
    const existingIndex = this.items.findIndex(item => 
      item.variantId === productData.variantId
    );
    
    if (existingIndex === -1) {
      this.items.push({
        ...productData,
        addedAt: new Date().toISOString(),
        position: this.items.length
      });
      this.saveWishlist();
      this.showNotification('Item added to wishlist');
      console.log('✅ Wishlist: Item added successfully. Total items:', this.items.length);
      
      // Dispatch custom event for Klaviyo tracking
      const event = new CustomEvent('wishlist:added', {
        detail: {
          variantId: productData.variantId,
          productId: productData.productId,
          productData: productData
        }
      });
      document.dispatchEvent(event);
      
      return true;
    } else {
      console.log('⚠️ Wishlist: Item already exists in wishlist');
    }
    return false;
  }
  
  removeItem(variantId) {
    const removedItem = this.items.find(item => item.variantId === variantId);
    this.items = this.items.filter(item => item.variantId !== variantId);
    this.reorderItems();
    this.saveWishlist();
    this.showNotification('Item removed from wishlist');
    
    // Dispatch custom event for Klaviyo tracking
    if (removedItem) {
      const event = new CustomEvent('wishlist:removed', {
        detail: {
          variantId: variantId,
          productId: removedItem.productId,
          productData: removedItem
        }
      });
      document.dispatchEvent(event);
    }
  }
  
  clearWishlist() {
    if (confirm('Are you sure you want to clear your wishlist?')) {
      this.items = [];
      this.saveWishlist();
      this.showNotification('Wishlist cleared');
    }
  }
  
  reorderItems() {
    this.items = this.items.map((item, index) => ({
      ...item,
      position: index
    }));
  }
  
  // UI Updates
  updateUI() {
    this.updateCount();
    this.updateHeartButtons();
    this.renderWishlistPage();
  }
  
  updateCount() {
    const countElements = document.querySelectorAll('[data-wishlist-count]');
    countElements.forEach(el => {
      el.textContent = this.items.length;
      el.style.display = this.items.length > 0 ? 'inline-block' : 'none';
    });
  }
  
  updateHeartButtons() {
    const heartButtons = document.querySelectorAll('[data-wishlist-toggle]');
    console.log('❤️ Wishlist: Updating', heartButtons.length, 'heart buttons');
    
    heartButtons.forEach(button => {
      const variantId = button.getAttribute('data-variant-id');
      const isInWishlist = this.items.some(item => item.variantId === variantId);
      button.classList.toggle('is-active', isInWishlist);
      button.setAttribute('aria-pressed', isInWishlist);
      
      if (isInWishlist) {
        console.log('✅ Heart active for variant:', variantId);
      }
    });
  }
  
  renderWishlistPage() {
    const container = document.querySelector('[data-wishlist-items]');
    const emptyState = document.querySelector('[data-wishlist-empty]');
    
    if (!container) return;
    
    if (this.items.length === 0) {
      container.style.display = 'none';
      if (emptyState) emptyState.style.display = 'block';
      return;
    }
    
    container.style.display = 'grid';
    if (emptyState) emptyState.style.display = 'none';
    
    container.innerHTML = this.items.map(item => `
      <div class="wishlist-item" data-wishlist-item="${item.variantId}" data-position="${item.position}">
        <div class="wishlist-item__image">
          <a href="${item.url}">
            <img src="${item.image}" alt="${item.title}" loading="lazy">
          </a>
        </div>
        <div class="wishlist-item__details">
          <h3 class="wishlist-item__title">
            <a href="${item.url}">${item.title}</a>
          </h3>
          ${item.variantTitle ? `<p class="wishlist-item__variant">${item.variantTitle}</p>` : ''}
          <p class="wishlist-item__price">${this.formatMoney(item.price)}</p>
          <p class="wishlist-item__added">Added ${this.formatDate(item.addedAt)}</p>
        </div>
        <div class="wishlist-item__actions">
          <form action="/cart/add" method="post" class="wishlist-add-to-cart">
            <input type="hidden" name="id" value="${item.variantId}">
            <button type="submit" class="btn btn--primary btn--small">Add to Cart</button>
          </form>
          <button class="wishlist-item__remove" data-wishlist-remove="${item.variantId}">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2"/>
            </svg>
          </button>
        </div>
      </div>
    `).join('');
    
    // Initialize drag and drop if on wishlist page
    if (container.closest('.wishlist-page')) {
      this.initializeSortable(container);
    }
  }
  
  initializeSortable(container) {
    if (typeof Sortable === 'undefined') {
      // Load SortableJS dynamically
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/Sortable/1.15.0/Sortable.min.js';
      script.onload = () => this.setupSortable(container);
      document.head.appendChild(script);
    } else {
      this.setupSortable(container);
    }
  }
  
  setupSortable(container) {
    Sortable.create(container, {
      animation: 150,
      handle: '.wishlist-item__image',
      onEnd: (evt) => {
        const itemElements = container.querySelectorAll('[data-wishlist-item]');
        const newOrder = Array.from(itemElements).map(el => 
          el.getAttribute('data-wishlist-item')
        );
        
        // Reorder items array based on new positions
        const reorderedItems = [];
        newOrder.forEach((variantId, index) => {
          const item = this.items.find(i => i.variantId === variantId);
          if (item) {
            reorderedItems.push({ ...item, position: index });
          }
        });
        
        this.items = reorderedItems;
        this.saveWishlist();
      }
    });
  }
  
  // Event Handlers
  bindEvents() {
    console.log('🎯 Wishlist: Binding events...');
    
    // Toggle wishlist items
    document.addEventListener('click', (e) => {
      const toggleBtn = e.target.closest('[data-wishlist-toggle]');
      if (toggleBtn) {
        console.log('👆 Wishlist: Toggle button clicked');
        e.preventDefault();
        this.handleToggle(toggleBtn);
      }
      
      const removeBtn = e.target.closest('[data-wishlist-remove]');
      if (removeBtn) {
        e.preventDefault();
        const variantId = removeBtn.getAttribute('data-wishlist-remove');
        this.removeItem(variantId);
      }
      
      const clearBtn = e.target.closest('[data-wishlist-clear]');
      if (clearBtn) {
        e.preventDefault();
        this.clearWishlist();
      }
      
      const shareBtn = e.target.closest('[data-wishlist-share]');
      if (shareBtn) {
        e.preventDefault();
        this.showShareModal();
      }
      
      const copyBtn = e.target.closest('[data-copy-link]');
      if (copyBtn) {
        e.preventDefault();
        this.copyShareLink();
      }
      
      const closeModal = e.target.closest('[data-close-modal]');
      if (closeModal) {
        e.preventDefault();
        this.hideShareModal();
      }
    });
    
    // Handle add to cart from wishlist
    document.addEventListener('submit', (e) => {
      if (e.target.classList.contains('wishlist-add-to-cart')) {
        e.preventDefault();
        this.addToCart(e.target);
      }
    });
  }
  
  handleToggle(button) {
    const variantId = button.getAttribute('data-variant-id');
    const isInWishlist = this.items.some(item => item.variantId === variantId);
    
    console.log('🔄 Wishlist: Toggling variant', variantId, 'Currently in wishlist:', isInWishlist);
    
    if (isInWishlist) {
      this.removeItem(variantId);
    } else {
      // Gather product data
      const productData = {
        variantId: variantId,
        productId: button.getAttribute('data-product-id'),
        title: button.getAttribute('data-product-title'),
        variantTitle: button.getAttribute('data-variant-title'),
        price: parseInt(button.getAttribute('data-price')),
        comparePrice: parseInt(button.getAttribute('data-compare-price')),
        image: button.getAttribute('data-image'),
        url: button.getAttribute('data-url'),
        vendor: button.getAttribute('data-vendor'),
        sku: button.getAttribute('data-sku')
      };
      
      console.log('📦 Wishlist: Product data collected', productData);
      this.addItem(productData);
    }
  }
  
  // Klaviyo Integration
  syncWithKlaviyo() {
    if (typeof klaviyo === 'undefined') {
      console.log('⚠️ Wishlist: Klaviyo not found, skipping sync');
      return;
    }
    
    const customerId = window.ShopifyAnalytics?.meta?.page?.customerId;
    const customerEmail = this.getCustomerEmail();
    
    if (!customerEmail && !customerId) {
      console.log('ℹ️ Wishlist: No customer email or ID, skipping Klaviyo sync');
      return;
    }
    
    console.log('🔄 Wishlist: Syncing with Klaviyo...');
    
    // Calculate wishlist metrics
    const wishlistValue = this.items.reduce((sum, item) => sum + item.price, 0);
    const wishlistProductIds = this.items.map(item => item.productId);
    const wishlistVariantIds = this.items.map(item => item.variantId);
    
    // Prepare identify data
    const identifyData = {
      wishlist_items: wishlistVariantIds,
      wishlist_products: wishlistProductIds,
      wishlist_count: this.items.length,
      wishlist_value: wishlistValue / 100, // Convert from cents
      wishlist_last_updated: new Date().toISOString()
    };
    
    if (customerEmail) {
      identifyData.email = customerEmail;
      identifyData.$email = customerEmail;
    }
    
    console.log('📊 Wishlist: Klaviyo identify data', identifyData);
    
    // Use array syntax for compatibility with your current implementation
    if (window._klOnsite || Array.isArray(klaviyo)) {
      // Old Klaviyo syntax
      klaviyo.push(['identify', identifyData]);
      
      // Track wishlist updated event
      if (this.isInitialized) {
        const trackData = {
          action: 'updated',
          item_count: this.items.length,
          total_value: wishlistValue / 100,
          $value: wishlistValue / 100, // Include $value for revenue tracking
          items: this.items.map(item => ({
            ProductID: item.productId, // Match your naming convention
            VariantID: item.variantId,
            Name: item.title,
            Price: item.price / 100,
            SKU: item.sku,
            ImageURL: item.image,
            URL: item.url
          }))
        };
        
        console.log('📊 Wishlist: Klaviyo track data', trackData);
        klaviyo.push(['track', 'Wishlist Updated', trackData]);
      }
    } else {
      // New Klaviyo SDK syntax (fallback)
      klaviyo.identify(identifyData);
      
      if (this.isInitialized) {
        klaviyo.track('Wishlist Updated', {
          action: 'updated',
          item_count: this.items.length,
          total_value: wishlistValue / 100,
          $value: wishlistValue / 100,
          items: this.items.map(item => ({
            ProductID: item.productId,
            VariantID: item.variantId,
            Name: item.title,
            Price: item.price / 100,
            SKU: item.sku,
            ImageURL: item.image,
            URL: item.url
          }))
        });
      }
    }
    
    console.log('✅ Wishlist: Klaviyo sync complete');
  }
  
  getCustomerEmail() {
    // Try to get email from various sources
    // This would need to be implemented based on your theme
    return window.customerEmail || null;
  }
  
  // Share Functionality
  generateShareUrl() {
    // Just store variant IDs - much shorter!
    const variantIds = this.items.map(item => item.variantId).join(',');
    
    // Use URL-safe base64 encoding
    const encoded = btoa(variantIds)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
    
    // Build URL with UTM parameters
    const baseUrl = `${window.location.origin}/pages/wishlist`;
    const params = new URLSearchParams({
      share: encoded,
      utm_source: 'wishlist_share',
      utm_medium: 'social',
      utm_campaign: 'wishlist_sharing',
      utm_content: `${this.items.length}_items`
    });
    
    // Add customer ID if available (anonymized)
    const customerId = window.ShopifyAnalytics?.meta?.page?.customerId;
    if (customerId) {
      // Hash the customer ID for privacy
      const hashedId = btoa(customerId).substring(0, 8);
      params.append('utm_term', `user_${hashedId}`);
    }
    
    console.log('🔗 Wishlist: Generating share URL with', this.items.length, 'items');
    console.log('📏 Share URL length:', `${baseUrl}?${params.toString()}`.length, 'characters');
    return `${baseUrl}?${params.toString()}`;
  }
  
  showShareModal() {
    const modal = document.querySelector('[data-share-modal]');
    const urlInput = modal.querySelector('.wishlist-share-url');
    
    // Generate base share URL
    const baseUrl = this.generateShareUrl();
    urlInput.value = baseUrl;
    
    // Add platform-specific share buttons if they don't exist
    if (!modal.querySelector('.wishlist-share-platforms')) {
      this.addSharePlatforms(modal);
    }
    
    modal.style.display = 'flex';
  }
  
  addSharePlatforms(modal) {
    const modalContent = modal.querySelector('.wishlist-share-modal__content');
    const platformsDiv = document.createElement('div');
    platformsDiv.className = 'wishlist-share-platforms';
    platformsDiv.innerHTML = `
      <h4>Share via:</h4>
      <div class="wishlist-share-buttons">
        <button class="share-platform-btn" data-platform="email">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3.293 3.293 3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"/>
          </svg>
          Email
        </button>
        <button class="share-platform-btn" data-platform="whatsapp">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M17.05 2.95A9.93 9.93 0 0010 0 9.94 9.94 0 001.44 15.56L0 20l4.55-1.44A9.94 9.94 0 0010 20a9.94 9.94 0 007.05-17.05zM10 18.33A8.27 8.27 0 015.5 16.9l-.3-.18-3.12.99.99-3.12-.18-.3A8.28 8.28 0 1110 18.33zm4.54-6.2c-.25-.12-1.47-.72-1.7-.8-.23-.09-.4-.13-.56.12-.17.25-.64.8-.78.97-.15.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.38-1.72-.15-.25-.02-.38.11-.51.11-.11.25-.29.37-.44.13-.15.17-.25.25-.42.09-.17.04-.31-.02-.44-.06-.12-.56-1.35-.77-1.85-.2-.49-.41-.42-.56-.43-.15 0-.31-.02-.48-.02-.17 0-.44.06-.67.31-.23.25-.88.86-.88 2.1 0 1.24.9 2.44 1.02 2.6.13.17 1.77 2.7 4.28 3.79.6.26 1.07.41 1.43.53.6.19 1.15.16 1.58.1.48-.07 1.47-.6 1.68-1.18.2-.58.2-1.08.15-1.18-.06-.1-.23-.17-.48-.29z"/>
          </svg>
          WhatsApp
        </button>
        <button class="share-platform-btn" data-platform="facebook">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M20 10c0-5.523-4.477-10-10-10S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z"/>
          </svg>
          Facebook
        </button>
        <button class="share-platform-btn" data-platform="twitter">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84"/>
          </svg>
          Twitter
        </button>
        <button class="share-platform-btn" data-platform="pinterest">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 0C4.478 0 0 4.477 0 10c0 4.237 2.636 7.855 6.356 9.312-.088-.791-.167-2.005.035-2.868.181-.78 1.172-4.97 1.172-4.97s-.299-.6-.299-1.486c0-1.39.806-2.428 1.81-2.428.852 0 1.264.64 1.264 1.408 0 .858-.546 2.14-.828 3.33-.236.995.5 1.807 1.48 1.807 1.778 0 3.144-1.874 3.144-4.58 0-2.393-1.72-4.068-4.177-4.068-2.845 0-4.515 2.135-4.515 4.34 0 .859.33 1.781.744 2.281a.3.3 0 01.069.288l-.278 1.133c-.044.183-.145.223-.335.134-1.249-.581-2.03-2.407-2.03-3.874 0-3.154 2.292-6.052 6.608-6.052 3.469 0 6.165 2.473 6.165 5.776 0 3.447-2.173 6.22-5.19 6.22-1.013 0-1.965-.525-2.291-1.148l-.623 2.378c-.226.869-.835 1.958-1.244 2.621.937.29 1.931.446 2.962.446 5.523 0 10-4.477 10-10S15.523 0 10 0z"/>
          </svg>
          Pinterest
        </button>
      </div>
    `;
    
    // Insert before the close button
    const closeBtn = modalContent.querySelector('[data-close-modal]');
    modalContent.insertBefore(platformsDiv, closeBtn);
    
    // Add event listeners for platform buttons
    platformsDiv.addEventListener('click', (e) => {
      const btn = e.target.closest('.share-platform-btn');
      if (btn) {
        const platform = btn.getAttribute('data-platform');
        this.shareOnPlatform(platform);
      }
    });
  }
  
  shareOnPlatform(platform) {
    const baseUrl = this.generateShareUrl();
    const wishlistTitle = `Check out my wishlist with ${this.items.length} items!`;
    const wishlistDescription = `I've saved ${this.items.length} amazing items to my wishlist. Take a look!`;
    
    // Update UTM medium based on platform
    const url = new URL(baseUrl);
    url.searchParams.set('utm_medium', platform);
    
    let shareUrl;
    
    switch(platform) {
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(wishlistTitle)}&body=${encodeURIComponent(wishlistDescription + '\n\n' + url.toString())}`;
        break;
        
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(wishlistDescription + ' ' + url.toString())}`;
        break;
        
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url.toString())}`;
        break;
        
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(wishlistTitle)}&url=${encodeURIComponent(url.toString())}`;
        break;
        
      case 'pinterest':
        // Use the first item's image for Pinterest
        const firstImage = this.items[0]?.image || '';
        shareUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url.toString())}&media=${encodeURIComponent(firstImage)}&description=${encodeURIComponent(wishlistDescription)}`;
        break;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
      
      // Track share event in Klaviyo
      if (typeof klaviyo !== 'undefined') {
        klaviyo.push(['track', 'Shared Wishlist', {
          platform: platform,
          item_count: this.items.length,
          total_value: this.items.reduce((sum, item) => sum + item.price, 0) / 100
        }]);
      }
    }
  }
  
  hideShareModal() {
    const modal = document.querySelector('[data-share-modal]');
    modal.style.display = 'none';
  }
  
  copyShareLink() {
    const urlInput = document.querySelector('.wishlist-share-url');
    urlInput.select();
    document.execCommand('copy');
    this.showNotification('Link copied to clipboard!');
  }
  
  checkSharedWishlist() {
    const urlParams = new URLSearchParams(window.location.search);
    const shareData = urlParams.get('share');
    
    if (shareData) {
      console.log('📨 Wishlist: Found shared wishlist in URL');
      try {
        // Decode URL-safe base64
        const paddedData = shareData
          .replace(/-/g, '+')
          .replace(/_/g, '/')
          .padEnd(shareData.length + (4 - shareData.length % 4) % 4, '=');
        
        const decodedString = atob(paddedData);
        const variantIds = decodedString.split(',').filter(id => id);
        
        console.log('📦 Wishlist: Decoded variant IDs:', variantIds);
        
        if (variantIds.length > 0) {
          this.loadSharedWishlist(variantIds);
        }
      } catch (e) {
        console.error('❌ Wishlist: Invalid share data', e);
        this.showNotification('Invalid wishlist link');
      }
    }
  }
  
  async loadSharedWishlist(variantIds) {
    console.log('🔄 Wishlist: Fetching product data for shared wishlist...');
    this.showNotification('Loading shared wishlist...');
    
    try {
      // Fetch product data for all variants
      const productDataPromises = variantIds.map(variantId => 
        this.fetchVariantData(variantId)
      );
      
      const productData = await Promise.all(productDataPromises);
      const validProducts = productData.filter(data => data !== null);
      
      console.log('✅ Wishlist: Fetched', validProducts.length, 'valid products');
      
      if (validProducts.length > 0) {
        this.handleSharedWishlistData(validProducts);
      } else {
        this.showNotification('No valid products found in shared wishlist');
      }
    } catch (error) {
      console.error('❌ Wishlist: Error loading shared wishlist', error);
      this.showNotification('Error loading shared wishlist');
    }
  }
  
  async fetchVariantData(variantId) {
    try {
      // Method 1: Try to get from cart API (doesn't require product handle)
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '0');
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        console.warn('⚠️ Wishlist: Variant not found:', variantId);
        return null;
      }
      
      const data = await response.json();
      
      // Format the data to match our wishlist structure
      return {
        variantId: variantId,
        productId: data.product_id?.toString() || '',
        title: data.product_title || 'Product',
        variantTitle: data.variant_title || '',
        price: data.price || 0,
        image: data.image || data.featured_image || '',
        url: data.url || `/products/${data.handle}?variant=${variantId}`,
        vendor: data.vendor || '',
        sku: data.sku || ''
      };
      
    } catch (error) {
      console.error('❌ Wishlist: Error fetching variant', variantId, error);
      
      // Method 2: Fallback - try to construct basic data
      return {
        variantId: variantId,
        productId: '',
        title: `Product (${variantId})`,
        variantTitle: '',
        price: 0,
        image: '',
        url: `/products/?variant=${variantId}`,
        vendor: '',
        sku: ''
      };
    }
  }
  
  handleSharedWishlistData(sharedProducts) {
    // Check if user already has items in their wishlist
    const hasExistingItems = this.items.length > 0;
    
    if (hasExistingItems) {
      // Create a custom modal for better UX
      const modal = document.createElement('div');
      modal.className = 'wishlist-share-choice-modal';
      modal.innerHTML = `
        <div class="wishlist-share-choice-content">
          <h3>Shared Wishlist Found</h3>
          <p>This shared wishlist contains ${sharedProducts.length} items.</p>
          <p>You currently have ${this.items.length} items in your wishlist.</p>
          <p>What would you like to do?</p>
          <div class="wishlist-share-choice-buttons">
            <button class="btn btn--primary" data-action="replace">View Shared Wishlist</button>
            <button class="btn btn--secondary" data-action="merge">Add to My Wishlist</button>
            <button class="btn btn--outline" data-action="cancel">Cancel</button>
          </div>
        </div>
      `;
      
      document.body.appendChild(modal);
      
      // Handle button clicks
      modal.addEventListener('click', (e) => {
        const action = e.target.getAttribute('data-action');
        
        if (action === 'replace') {
          this.loadSharedItems(sharedProducts, 'replace');
          modal.remove();
        } else if (action === 'merge') {
          this.loadSharedItems(sharedProducts, 'merge');
          modal.remove();
        } else if (action === 'cancel') {
          modal.remove();
          // Remove share parameter from URL
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
        }
      });
    } else {
      // No existing items, just load the shared wishlist
      this.loadSharedItems(sharedProducts, 'replace');
    }
  }
  
  loadSharedItems(sharedProducts, mode = 'replace') {
    console.log(`📥 Wishlist: Loading shared wishlist (${mode} mode)`);
    
    const newItems = sharedProducts.map((product, index) => ({
      ...product,
      addedAt: new Date().toISOString(),
      position: mode === 'merge' ? this.items.length + index : index
    }));
    
    if (mode === 'replace') {
      this.items = newItems;
      this.showNotification(`Loaded ${newItems.length} items from shared wishlist`);
    } else {
      // Merge - avoid duplicates
      let addedCount = 0;
      newItems.forEach(newItem => {
        if (!this.items.find(item => item.variantId === newItem.variantId)) {
          this.items.push(newItem);
          addedCount++;
        }
      });
      this.showNotification(`Added ${addedCount} new items to your wishlist`);
    }
    
    // Save and update
    this.saveWishlist();
    
    // Remove share parameter from URL
    const newUrl = window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);
  }
  
  // Server Sync (for logged-in users)
  async syncWithServer() {
    console.log('🔄 Wishlist: Checking if server sync is needed...');
    
    // For now, disable server sync until you implement the backend
    // Remove this return statement when you have a server endpoint
    console.log('ℹ️ Wishlist: Server sync disabled (no endpoint yet)');
    return;
    
    try {
      const response = await fetch('/apps/wishlist/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: this.items,
          action: 'sync'
        })
      });
      
      if (response.ok) {
        const serverData = await response.json();
        // Merge server data with local data
        this.mergeWishlistData(serverData);
      }
    } catch (error) {
      console.error('Failed to sync with server:', error);
    }
  }
  
  mergeWishlistData(serverData) {
    // Implement merge logic based on your requirements
    // This is a simple example that prioritizes server data
    if (serverData.items && serverData.items.length > 0) {
      this.items = serverData.items;
      this.saveWishlist();
    }
  }
  
  // Utility Functions
  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify.currency.active || 'USD'
    }).format(cents / 100);
  }
  
  formatDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  }
  
  showNotification(message) {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'wishlist-notification';
    notification.textContent = message;
    document.body.appendChild(notification);
    
    // Show notification
    setTimeout(() => notification.classList.add('is-visible'), 10);
    
    // Hide and remove after 3 seconds
    setTimeout(() => {
      notification.classList.remove('is-visible');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }
  
  async addToCart(form) {
    const formData = new FormData(form);
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        this.showNotification('Added to cart!');
        // Optionally remove from wishlist
        // const variantId = formData.get('id');
        // this.removeItem(variantId);
        
        // Update cart count if you have cart functionality
        if (window.updateCartCount) {
          window.updateCartCount();
        }
      }
    } catch (error) {
      this.showNotification('Failed to add to cart');
    }
  }
}

  // Initialize wishlist when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (!window.wishlist) {
        window.wishlist = new WishlistManager();
      }
    });
  } else {
    if (!window.wishlist) {
      window.wishlist = new WishlistManager();
    }
  }
  
  // Expose class to window for potential external use
  window.WishlistManager = WishlistManager;
}